import { useState, useEffect, useCallback, useRef } from 'react'
import { Radio, Activity, Users, Tv, Settings, RefreshCw, AlertTriangle, CheckCircle, XCircle, Eye, Clock, DollarSign, Zap, ArrowRight, Pause } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import './SSAIManager.css'

const SSAI_SERVER = 'https://tbtv-ssai-m9o4q.ondigitalocean.app'

export default function SSAIManager() {
  const { api } = useAuth()
  const [health, setHealth] = useState(null)
  const [channels, setChannels] = useState([])
  const [proxyStatus, setProxyStatus] = useState({}) // channelId → { ads_enabled, vast_tag_url }
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [channelStatus, setChannelStatus] = useState(null)
  const [channelSchedule, setChannelSchedule] = useState(null)
  const [manifest, setManifest] = useState(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef(null)

  // Bumper library
  const [bumpers, setBumpers] = useState([])
  const [newBumperName, setNewBumperName] = useState('')
  const [newBumperUrl, setNewBumperUrl] = useState('')
  const [newBumperFormat, setNewBumperFormat] = useState('mux')
  const [newBumperDuration, setNewBumperDuration] = useState('')
  const [channelBumperIds, setChannelBumperIds] = useState([])
  const [channelBumperEnabled, setChannelBumperEnabled] = useState(false)
  const [channelBumperPosition, setChannelBumperPosition] = useState('both')
  const [bumperSaving, setBumperSaving] = useState(false)
  const [bumperStatus, setBumperStatus] = useState('')
  // Video picker for bumper add
  const [bumperPickerOpen, setBumperPickerOpen] = useState(false)
  const [bumperPickerSource, setBumperPickerSource] = useState('mux') // 'mux' | 'cf'
  const [bumperPickerSearch, setBumperPickerSearch] = useState('')
  const [bumperPickerResults, setBumperPickerResults] = useState([])
  const [bumperPickerLoading, setBumperPickerLoading] = useState(false)

  // SSAI global settings
  const [ssaiSettings, setSsaiSettings] = useState({
    vast_rotation_minutes: 30,
    ad_slot_seconds: 30,
    session_ttl_minutes: 5,
    default_ad_interval_minutes: 8,
    default_bumper_enabled: false,
    default_bumper_position: 'both',
  })
  const [ssaiSettingsOpen, setSsaiSettingsOpen] = useState(false)
  const [ssaiSettingsSaving, setSsaiSettingsSaving] = useState(false)
  const [ssaiSettingsStatus, setSsaiSettingsStatus] = useState('')

  // Fetch server health
  const fetchHealth = useCallback(async () => {
    try {
      const resp = await fetch(`${SSAI_SERVER}/health`)
      if (resp.ok) setHealth(await resp.json())
      else setHealth({ status: 'error', error: `HTTP ${resp.status}` })
    } catch (err) {
      setHealth({ status: 'down', error: err.message })
    }
  }, [])

  // Fetch channel list from DO + proxy status from hls-stitcher
  const fetchChannels = useCallback(async () => {
    try {
      const resp = await fetch(`${SSAI_SERVER}/api/channels`)
      if (resp.ok) {
        const data = await resp.json()
        setChannels(data.channels || [])
      }
    } catch {}
    // Fetch proxy status from hls-stitcher (which channels have ads_enabled in the Worker)
    try {
      const data = await api('/playlists')
      const status = {}
      for (const ch of (data.channels || data || [])) {
        if (ch.vast_tag_url) {
          status[ch.id] = { ads_enabled: !!ch.ads_enabled, vast_tag_url: ch.vast_tag_url, ad_interval_seconds: ch.ad_interval_seconds }
        }
      }
      setProxyStatus(status)
    } catch {}
  }, [api])

  // Fetch selected channel status
  const fetchChannelStatus = useCallback(async (channelId) => {
    if (!channelId) return
    try {
      const resp = await fetch(`${SSAI_SERVER}/api/channels/${channelId}/status`)
      if (resp.ok) setChannelStatus(await resp.json())
    } catch (err) {
      setChannelStatus({ error: err.message })
    }
  }, [])

  // Fetch manifest for inspection
  const fetchManifest = useCallback(async (channelId) => {
    if (!channelId) return
    try {
      const resp = await fetch(`${SSAI_SERVER}/channel/${channelId}/stream/video_720.m3u8?sid=inspector`)
      if (resp.ok) {
        const text = await resp.text()
        setManifest(text)
      }
    } catch (err) {
      setManifest(`Error: ${err.message}`)
    }
  }, [])

  // Live probe: check if Worker is actually proxying to DO (or fallen back to non-SSAI)
  // Uses the DO server's health + channel status instead of hitting hls-stitcher directly
  // (avoids CORS issues and HEAD request limitations)
  const [liveProbe, setLiveProbe] = useState({}) // channelId → 'ssai' | 'non-ssai' | 'error'
  const probeChannel = useCallback(async (channelId) => {
    const proxy = proxyStatus[channelId]
    if (!proxy?.ads_enabled) return 'proxy-off'

    // Check if DO server is healthy and has this channel
    try {
      const resp = await fetch(`${SSAI_SERVER}/api/channels/${channelId}/status`, {
        signal: AbortSignal.timeout(3000),
      })
      if (!resp.ok) return 'non-ssai' // DO can't serve this channel → fallback

      const data = await resp.json()
      const hasIndices = data.indices && data.indices.length > 0
      const hasVast = data.vast && data.vast.streamId

      if (hasIndices && hasVast) return 'ssai' // DO ready, proxy on → ads serving
      if (hasIndices && !hasVast) return 'ssai-no-ad' // indices ready but no VAST creative
      return 'non-ssai' // no indices → fallback
    } catch {
      return 'non-ssai' // DO unreachable → Worker falls back to non-SSAI
    }
  }, [proxyStatus])

  const probeAllChannels = useCallback(async () => {
    const results = {}
    for (const ch of channels) {
      const proxy = proxyStatus[ch.id]
      if (proxy?.ads_enabled) {
        results[ch.id] = await probeChannel(ch.id)
      } else {
        results[ch.id] = 'proxy-off'
      }
    }
    setLiveProbe(results)
  }, [channels, proxyStatus, probeChannel])

  // Initial load
  useEffect(() => {
    fetchHealth()
    fetchChannels()
  }, [fetchHealth, fetchChannels])

  // Probe on initial load only (not auto-refresh — use Probe button for manual check)
  const [probing, setProbing] = useState(false)
  const handleProbe = async () => {
    setProbing(true)
    await probeAllChannels()
    setProbing(false)
  }

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchHealth()
        fetchChannels()
        if (selectedChannel) fetchChannelStatus(selectedChannel)
      }, 5000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [autoRefresh, selectedChannel, fetchHealth, fetchChannels, fetchChannelStatus])

  // Bumper library
  // SSAI settings load/save
  const fetchSsaiSettings = useCallback(async () => {
    try {
      const data = await api('/settings?prefix=ssai_')
      const s = data.settings || {}
      setSsaiSettings(prev => ({
        vast_rotation_minutes: parseInt(s.ssai_vast_rotation_minutes || prev.vast_rotation_minutes, 10),
        ad_slot_seconds: parseInt(s.ssai_ad_slot_seconds || prev.ad_slot_seconds, 10),
        session_ttl_minutes: parseInt(s.ssai_session_ttl_minutes || prev.session_ttl_minutes, 10),
        default_ad_interval_minutes: parseInt(s.ssai_default_ad_interval_minutes || prev.default_ad_interval_minutes, 10),
        default_bumper_enabled: s.ssai_default_bumper_enabled === 'true',
        default_bumper_position: s.ssai_default_bumper_position || prev.default_bumper_position,
      }))
    } catch {}
  }, [api])

  const handleSaveSsaiSettings = async () => {
    setSsaiSettingsSaving(true)
    setSsaiSettingsStatus('')
    try {
      await api('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          settings: {
            ssai_vast_rotation_minutes: String(ssaiSettings.vast_rotation_minutes),
            ssai_ad_slot_seconds: String(ssaiSettings.ad_slot_seconds),
            ssai_session_ttl_minutes: String(ssaiSettings.session_ttl_minutes),
            ssai_default_ad_interval_minutes: String(ssaiSettings.default_ad_interval_minutes),
            ssai_default_bumper_enabled: String(ssaiSettings.default_bumper_enabled),
            ssai_default_bumper_position: ssaiSettings.default_bumper_position,
          }
        })
      })
      setSsaiSettingsStatus('Settings saved. DO server will pick up changes on next rotation cycle.')
      setTimeout(() => setSsaiSettingsStatus(''), 5000)
    } catch (e) {
      setSsaiSettingsStatus(`Error: ${e.message}`)
    }
    setSsaiSettingsSaving(false)
  }

  useEffect(() => { fetchSsaiSettings() }, [fetchSsaiSettings])

  const fetchBumpers = useCallback(async () => {
    try {
      const data = await api('/ssai/bumpers')
      setBumpers(data.bumpers || [])
    } catch {}
  }, [api])

  const fetchChannelBumpers = useCallback(async (channelId) => {
    try {
      const data = await api(`/playlists/${channelId}/bumpers`)
      setChannelBumperIds((data.bumpers || []).map(b => b.bumper_id))
      setChannelBumperEnabled(data.bumperEnabled || false)
      setChannelBumperPosition(data.bumperPosition || 'both')
    } catch {}
  }, [api])

  useEffect(() => { fetchBumpers() }, [fetchBumpers])

  const searchBumperVideos = async () => {
    setBumperPickerLoading(true)
    try {
      if (bumperPickerSource === 'mux') {
        const data = await api(`/mux/assets?limit=20`)
        const assets = data.data || data.assets || []
        setBumperPickerResults(assets.map(a => ({
          id: a.id,
          name: a.passthrough || a.id.substring(0, 12),
          hls_url: a.playback_ids?.[0] ? `https://stream.mux.com/${a.playback_ids[0].id}.m3u8` : null,
          duration: a.duration,
          format: 'mux',
          thumbnail: a.playback_ids?.[0] ? `https://image.mux.com/${a.playback_ids[0].id}/thumbnail.jpg?width=160&height=90&time=1` : null,
        })).filter(v => v.hls_url))
      } else {
        const params = new URLSearchParams({ search: bumperPickerSearch, per_page: 20, page: 1 })
        const data = await api(`/videos?${params}`)
        let list = Array.isArray(data) ? data : (data.result || data.videos || data.data || [])
        if (!Array.isArray(list)) list = []
        setBumperPickerResults(list.map(v => ({
          id: v.uid,
          name: v.meta?.name || v.name || v.uid?.substring(0, 12),
          hls_url: `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${v.uid}/manifest/video.m3u8`,
          duration: v.duration,
          format: 'cf',
          thumbnail: `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${v.uid}/thumbnails/thumbnail.jpg?width=160&height=90&time=1s`,
        })))
      }
    } catch (e) {
      console.error('Bumper search failed:', e)
      setBumperPickerResults([])
    }
    setBumperPickerLoading(false)
  }

  const selectBumperFromPicker = async (video) => {
    const dur = video.duration ? Math.round(video.duration * 10) / 10 : null
    if (dur && (dur < 3 || dur > 5)) {
      if (!confirm(`⚠️ This video is ${dur}s. Bumpers should be 3-5 seconds for consistent EPG timing. Add anyway?`)) return
    }
    await api('/ssai/bumpers', {
      method: 'POST',
      body: JSON.stringify({
        name: video.name,
        hls_url: video.hls_url,
        format: video.format,
        duration_seconds: video.duration ? Math.round(video.duration * 10) / 10 : null,
        thumbnail_url: video.thumbnail || null,
      })
    })
    setBumperPickerOpen(false)
    setBumperPickerResults([])
    setBumperPickerSearch('')
    fetchBumpers()
  }

  const handleAddBumper = async () => {
    if (!newBumperName || !newBumperUrl) return
    const dur = parseFloat(newBumperDuration) || null
    if (dur && (dur < 3 || dur > 5)) {
      if (!confirm(`⚠️ Duration is ${dur}s. Bumpers should be 3-5 seconds for consistent EPG timing. Add anyway?`)) return
    }
    await api('/ssai/bumpers', {
      method: 'POST',
      body: JSON.stringify({ name: newBumperName, hls_url: newBumperUrl, format: newBumperFormat, duration_seconds: dur })
    })
    setNewBumperName(''); setNewBumperUrl(''); setNewBumperDuration('')
    fetchBumpers()
  }

  const handleDeleteBumper = async (id) => {
    if (!confirm('Delete this bumper?')) return
    await api(`/ssai/bumpers/${id}`, { method: 'DELETE' })
    fetchBumpers()
  }

  const handleSaveChannelBumpers = async () => {
    if (!selectedChannel) return

    // Validate bumper format matches channel content format
    if (channelBumperEnabled && channelBumperIds.length > 0 && channelStatus?.channel?.contentFormat) {
      const channelIsCF = channelStatus.channel.contentFormat.includes('CF')
      const selectedBumpers = bumpers.filter(b => channelBumperIds.includes(b.id))
      const mismatch = selectedBumpers.find(b => (channelIsCF && b.format === 'mux') || (!channelIsCF && b.format === 'cf'))
      if (mismatch) {
        setBumperStatus(`⚠️ Cannot use ${mismatch.format.toUpperCase()} bumper "${mismatch.name}" on a ${channelIsCF ? 'CF Stream' : 'Mux'} channel. Bumpers must match the channel format.`)
        return
      }
    }

    setBumperSaving(true)
    setBumperStatus('')
    try {
      await api(`/playlists/${selectedChannel}/bumpers`, {
        method: 'PUT',
        body: JSON.stringify({ bumperIds: channelBumperIds, bumperEnabled: channelBumperEnabled, bumperPosition: channelBumperPosition })
      })
      setBumperStatus('Bumper config saved. Indices will rebuild with bumpers.')
      setTimeout(() => setBumperStatus(''), 3000)
    } catch (e) {
      setBumperStatus(`Error: ${e.message}`)
    }
    setBumperSaving(false)
  }

  const toggleBumperForChannel = (bumperId) => {
    setChannelBumperIds(prev =>
      prev.includes(bumperId) ? prev.filter(id => id !== bumperId) : [...prev, bumperId]
    )
  }

  // Select channel
  const handleSelectChannel = (channelId) => {
    setSelectedChannel(channelId)
    setChannelStatus(null)
    setManifest(null)
    setChannelSchedule(null)
    fetchChannelStatus(channelId)
    fetchManifest(channelId)
    fetchChannelBumpers(channelId)
  }

  const serverUp = health && health.status === 'ok'

  // Parse manifest for display
  const manifestLines = manifest ? manifest.split('\n').map((line, i) => {
    let type = 'segment'
    if (line.startsWith('#EXT-X-DISCONTINUITY') && !line.includes('SEQUENCE')) type = 'disc'
    else if (line.startsWith('#EXT-X-CUE-OUT')) type = 'cue-out'
    else if (line.startsWith('#EXT-X-CUE-IN')) type = 'cue-in'
    else if (line.startsWith('#EXT-X-MAP')) type = 'map'
    else if (line.startsWith('#EXTM3U') || line.startsWith('#EXT-X-VERSION') || line.startsWith('#EXT-X-TARGET') || line.startsWith('#EXT-X-MEDIA-SEQ')) type = 'header'
    else if (line.startsWith('#EXTINF')) type = 'extinf'
    else if (line.startsWith('http')) type = 'url'
    else if (line.startsWith('#')) type = 'tag'
    return { line, type, num: i + 1 }
  }) : []

  return (
    <div className="ssai-mgr">
      {/* Header */}
      <div className="ssai-mgr-header">
        <h1><Radio size={20} /> SSAI Manager</h1>
        <div className="ssai-mgr-header-right">
          <label className="ssai-mgr-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh (5s)
          </label>
          <button className="btn btn-ghost btn-sm" onClick={handleProbe} disabled={probing} style={{ color: probing ? '#f59e0b' : '#4ec5c4' }}>
            <Activity size={14} /> {probing ? 'Probing...' : 'Probe Streams'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => { fetchHealth(); fetchChannels(); if (selectedChannel) fetchChannelStatus(selectedChannel) }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Server Status Bar */}
      <div className={`ssai-mgr-server ${serverUp ? 'up' : 'down'}`}>
        <div className="ssai-mgr-server-status">
          {serverUp ? <CheckCircle size={16} /> : <XCircle size={16} />}
          <strong>{serverUp ? 'Server Online' : 'Server Offline'}</strong>
          {health?.error && <span className="ssai-mgr-error">{health.error}</span>}
        </div>
        {serverUp && (
          <div className="ssai-mgr-server-stats">
            <span><Clock size={12} /> Uptime: {health.uptime}</span>
            <span><Tv size={12} /> Channels: {health.channels}</span>
            <span><Users size={12} /> Viewers: {Object.values(health.channelCounts || {}).reduce((a, b) => a + b, 0)}</span>
            <span><Zap size={12} /> Impressions: {health.totalImpressions}</span>
          </div>
        )}
      </div>

      <div className="ssai-mgr-layout">
        {/* SSAI Global Settings */}
        <div className="ssai-mgr-card" style={{ marginBottom: 12, gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setSsaiSettingsOpen(!ssaiSettingsOpen)}>
            <h4 style={{ margin: 0 }}><Settings size={14} /> SSAI Defaults</h4>
            <span style={{ fontSize: 11, color: '#999' }}>{ssaiSettingsOpen ? '▼' : '▶'}</span>
          </div>

          {ssaiSettingsOpen && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }} title="How often the SSAI server fetches a new ad creative from the VAST tag. Each rotation rebuilds the channel index with the new ad. Viewers see a different ad at the next break after rotation.">Ad Creative Rotation <span style={{ cursor: 'help', color: '#4ec5c4' }}>?</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={5} max={240} value={ssaiSettings.vast_rotation_minutes}
                      onChange={e => setSsaiSettings(s => ({ ...s, vast_rotation_minutes: parseInt(e.target.value) || 30 }))}
                      style={{ width: 60, padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                    <span style={{ fontSize: 11, color: '#999' }}>min</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3 }}>VAST refreshes every N minutes. Lower = more ad variety, higher = less CPU.</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }} title="Maximum duration of each ad break in seconds. Ads longer than this are truncated. This keeps the EPG schedule accurate — every ad break is exactly this duration.">Ad Slot Duration <span style={{ cursor: 'help', color: '#4ec5c4' }}>?</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={10} max={60} value={ssaiSettings.ad_slot_seconds}
                      onChange={e => setSsaiSettings(s => ({ ...s, ad_slot_seconds: parseInt(e.target.value) || 30 }))}
                      style={{ width: 60, padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                    <span style={{ fontSize: 11, color: '#999' }}>sec</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3 }}>Fixed slot size. Creatives over this are cut off. Affects EPG timing.</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }} title="How long a viewer session stays active after their last manifest request. After this timeout, the session expires and a new one is created on the next request. Affects impression counting accuracy.">Session Timeout <span style={{ cursor: 'help', color: '#4ec5c4' }}>?</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={1} max={30} value={ssaiSettings.session_ttl_minutes}
                      onChange={e => setSsaiSettings(s => ({ ...s, session_ttl_minutes: parseInt(e.target.value) || 5 }))}
                      style={{ width: 60, padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                    <span style={{ fontSize: 11, color: '#999' }}>min</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3 }}>Inactivity TTL per viewer. Too short = inflated session counts.</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }} title="Default time between ad breaks for newly created SSAI channels. Can be overridden per channel in Playlist Builder. Shorter intervals = more ads per hour.">Default Ad Interval <span style={{ cursor: 'help', color: '#4ec5c4' }}>?</span></label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={1} max={60} value={ssaiSettings.default_ad_interval_minutes}
                      onChange={e => setSsaiSettings(s => ({ ...s, default_ad_interval_minutes: parseInt(e.target.value) || 8 }))}
                      style={{ width: 60, padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                    <span style={{ fontSize: 11, color: '#999' }}>min</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3 }}>Per-channel override available in Playlist Builder.</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }} title="Whether new SSAI channels have bumpers enabled by default. Bumpers are short branded clips that play before and/or after each ad break. Must be same format (CF/Mux) as channel content.">Default Bumpers <span style={{ cursor: 'help', color: '#4ec5c4' }}>?</span></label>
                  <select value={ssaiSettings.default_bumper_enabled ? 'on' : 'off'}
                    onChange={e => setSsaiSettings(s => ({ ...s, default_bumper_enabled: e.target.value === 'on' }))}
                    style={{ padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                    <option value="off">Off</option>
                    <option value="on">On</option>
                  </select>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3 }}>Per-channel override in Bumper Configuration below.</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }} title="Where bumper clips play relative to the ad break. 'Before & After' plays a bumper entering and exiting each ad. 'Before Only' plays only the intro bumper. 'After Only' plays only the outro bumper.">Default Bumper Position <span style={{ cursor: 'help', color: '#4ec5c4' }}>?</span></label>
                  <select value={ssaiSettings.default_bumper_position}
                    onChange={e => setSsaiSettings(s => ({ ...s, default_bumper_position: e.target.value }))}
                    style={{ padding: '4px 8px', fontSize: 12, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                    <option value="both">Before & After</option>
                    <option value="before">Before Ad Only</option>
                    <option value="after">After Ad Only</option>
                  </select>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3 }}>Per-channel override in Bumper Configuration below.</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSaveSsaiSettings} disabled={ssaiSettingsSaving} style={{ fontSize: 11 }}>
                  {ssaiSettingsSaving ? 'Saving...' : 'Save Defaults'}
                </button>
                {ssaiSettingsStatus && (
                  <span style={{ fontSize: 11, color: ssaiSettingsStatus.startsWith('Error') ? '#e94560' : '#22c55e' }}>{ssaiSettingsStatus}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Channel List — merge DO channels + vader proxy channels */}
        <div className="ssai-mgr-channels">
          <h3>SSAI Channels</h3>
          {(() => {
            // Merge: all DO channels + any vader channels with ads_enabled not yet on DO
            const doIds = new Set(channels.map(c => c.id))
            const extraFromVader = Object.entries(proxyStatus)
              .filter(([id, p]) => p.ads_enabled && !doIds.has(id))
              .map(([id, p]) => ({ id, name: id, enabled: false, _vaderOnly: true }))
            const mergedChannels = [...channels, ...extraFromVader]
            if (mergedChannels.length === 0) return <div className="ssai-mgr-empty">No SSAI channels configured</div>
            return mergedChannels.map(ch => {
            const proxy = proxyStatus[ch.id]
            const proxyActive = proxy?.ads_enabled
            return (
              <div
                key={ch.id}
                className={`ssai-mgr-channel-item ${selectedChannel === ch.id ? 'selected' : ''}`}
                onClick={() => handleSelectChannel(ch.id)}
              >
                <div className="ssai-mgr-channel-name">
                  {proxyActive ? <ArrowRight size={12} style={{ color: '#22c55e' }} /> : <Pause size={12} style={{ color: '#f59e0b' }} />}
                  {ch.name}
                </div>
                <div className="ssai-mgr-channel-badges">
                  {liveProbe[ch.id] === 'ssai' && <span className="ssai-mgr-mini-badge live-ssai">LIVE SSAI</span>}
                  {liveProbe[ch.id] === 'ssai-no-ad' && <span className="ssai-mgr-mini-badge proxy-on">SSAI (no ad)</span>}
                  {liveProbe[ch.id] === 'non-ssai' && proxyActive && <span className="ssai-mgr-mini-badge live-fallback">FALLBACK</span>}
                  {liveProbe[ch.id] === 'non-ssai' && !proxyActive && <span className="ssai-mgr-mini-badge proxy-off">NON-SSAI</span>}
                  {liveProbe[ch.id] === 'proxy-off' && <span className="ssai-mgr-mini-badge proxy-off">PROXY OFF</span>}
                  {liveProbe[ch.id] === 'error' && <span className="ssai-mgr-mini-badge live-error">ERROR</span>}
                  {!liveProbe[ch.id] && <span className="ssai-mgr-mini-badge do-off">—</span>}
                  <span className={`ssai-mgr-mini-badge ${ch.enabled ? 'do-on' : 'do-off'}`}>
                    DO {ch.enabled ? 'READY' : 'OFF'}
                  </span>
                </div>
                <div className="ssai-mgr-channel-meta">
                  <span><Users size={10} /> {ch.viewers}</span>
                  <span>{ch.itemCount} videos</span>
                  {ch.vastStreamId && <span style={{ color: '#22c55e' }}>Ad loaded</span>}
                </div>
              </div>
            )
          })
          })()}
        </div>

        {/* Channel Detail */}
        <div className="ssai-mgr-detail">
          {!selectedChannel ? (
            <div className="ssai-mgr-empty-detail">
              <Tv size={48} style={{ opacity: 0.2 }} />
              <p>Select a channel to view SSAI details</p>
            </div>
          ) : channelStatus?.error ? (
            <div className="ssai-mgr-error-panel">
              <AlertTriangle size={20} /> Error loading channel: {channelStatus.error}
            </div>
          ) : !channelStatus ? (
            <div className="ssai-mgr-loading">Loading...</div>
          ) : (
            <>
              {/* Channel Info Bar */}
              {(() => {
                const proxy = proxyStatus[selectedChannel]
                const proxyActive = proxy?.ads_enabled
                const doReady = channelStatus.channel?.enabled
                const probe = liveProbe[selectedChannel]
                // Determine overall state using live probe
                let state, stateColor, stateDesc
                if (probe === 'ssai') {
                  state = 'LIVE — Ads Serving'
                  stateColor = '#22c55e'
                  stateDesc = 'Worker proxy ON → DO server READY with ad creative → Ads are being stitched into the stream.'
                } else if (probe === 'ssai-no-ad') {
                  state = 'LIVE — No Ad Creative'
                  stateColor = '#f59e0b'
                  stateDesc = 'Worker proxy ON → DO server has indices but VAST returned no ad. Ad breaks will be empty until a creative loads.'
                } else if (probe === 'non-ssai' && proxyActive) {
                  state = 'FALLBACK — DO Unreachable'
                  stateColor = '#ef4444'
                  stateDesc = 'Worker proxy is ON but DO server is not responding. Stream is playing WITHOUT ads (non-SSAI fallback). Check DO server health.'
                } else if (!proxyActive && doReady) {
                  state = 'STANDBY — Proxy Off'
                  stateColor = '#f59e0b'
                  stateDesc = 'DO server has indices ready, but Worker proxy is OFF (ads_enabled=0 in Playlist Builder). Enable SSAI in Playlist Builder to start serving ads.'
                } else if (probe === 'non-ssai' && !proxyActive) {
                  state = 'NON-SSAI'
                  stateColor = '#666'
                  stateDesc = 'SSAI disabled in Playlist Builder. Stream playing without ads.'
                } else if (probe === 'error') {
                  state = 'ERROR — Stream Unreachable'
                  stateColor = '#ef4444'
                  stateDesc = 'Could not reach the stream URL. Check hls-stitcher Worker.'
                } else {
                  state = 'CHECKING...'
                  stateColor = '#666'
                  stateDesc = 'Probing stream to determine SSAI status...'
                }
                return (
                  <div className="ssai-mgr-channel-header">
                    <div>
                      <h2>{channelStatus.channel?.name}</h2>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{stateDesc}</div>
                    </div>
                    <div className="ssai-mgr-badges">
                      <span className="ssai-mgr-badge" style={{ background: `${stateColor}15`, color: stateColor, border: `1px solid ${stateColor}40` }}>
                        {state}
                      </span>
                      <span className={`ssai-mgr-badge ${proxyActive ? 'active' : 'inactive'}`}>
                        Proxy: {proxyActive ? 'ON' : 'OFF'}
                      </span>
                      <span className="ssai-mgr-badge info">
                        {channelStatus.channel?.adIntervalSeconds ? `${Math.round(channelStatus.channel.adIntervalSeconds / 60)} min interval` : ''}
                      </span>
                      <span className="ssai-mgr-badge viewers">
                        <Users size={10} /> {channelStatus.viewers || 0} sessions
                      </span>
                      <span className="ssai-mgr-badge info">
                        <Eye size={10} /> {channelStatus.uniqueViewers || 0} unique
                      </span>
                      {channelStatus.channel?.contentFormat && (
                        <span className="ssai-mgr-badge" style={{
                          background: channelStatus.channel.contentFormat.includes('CF') ? 'rgba(249,115,22,0.15)' : 'rgba(168,85,247,0.15)',
                          color: channelStatus.channel.contentFormat.includes('CF') ? '#f97316' : '#a855f7',
                          border: `1px solid ${channelStatus.channel.contentFormat.includes('CF') ? 'rgba(249,115,22,0.3)' : 'rgba(168,85,247,0.3)'}`,
                        }}>
                          {channelStatus.channel.contentFormat}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Stats Grid */}
              <div className="ssai-mgr-stats-grid">
                {/* VAST Status */}
                <div className="ssai-mgr-card">
                  <h4><Activity size={14} /> VAST Ad Creative</h4>
                  {channelStatus.vast ? (
                    <div>
                      <div className="ssai-mgr-stat-row">
                        <span>Status</span>
                        <span style={{ color: channelStatus.vast.streamId ? '#22c55e' : '#f59e0b' }}>
                          {channelStatus.vast.streamId ? 'Loaded' : 'No ad'}
                        </span>
                      </div>
                      <div className="ssai-mgr-stat-row">
                        <span>Type</span>
                        <span>{channelStatus.vast.adType || '—'}</span>
                      </div>
                      <div className="ssai-mgr-stat-row">
                        <span>Age</span>
                        <span>{channelStatus.vast.age || '—'}</span>
                      </div>
                      {channelStatus.vast.adTitle && (
                        <div className="ssai-mgr-stat-row">
                          <span>Ad Creative</span>
                          <span style={{ color: '#eee' }}>{channelStatus.vast.adTitle}</span>
                        </div>
                      )}
                      {channelStatus.vast.duration && (
                        <div className="ssai-mgr-stat-row">
                          <span>Ad Duration</span>
                          <span>{channelStatus.vast.duration}s</span>
                        </div>
                      )}
                      {channelStatus.vast.streamId && (
                        <div className="ssai-mgr-stat-row">
                          <span>Stream ID</span>
                          <code className="ssai-mgr-code">{channelStatus.vast.streamId.substring(0, 16)}...</code>
                        </div>
                      )}
                    </div>
                  ) : <div className="ssai-mgr-muted">No VAST configured</div>}
                </div>

                {/* Indices */}
                <div className="ssai-mgr-card">
                  <h4><Tv size={14} /> Segment Indices</h4>
                  {channelStatus.indices?.length > 0 ? (
                    <div>
                      {channelStatus.indices.map((idx, i) => (
                        <div key={i} className="ssai-mgr-stat-row">
                          <span>{idx.track}</span>
                          <span>{idx.segments} segs / {idx.totalDuration}</span>
                        </div>
                      ))}
                    </div>
                  ) : <div className="ssai-mgr-muted">No indices built</div>}
                </div>

                {/* Sessions */}
                <div className="ssai-mgr-card">
                  <h4><Users size={14} /> Active Sessions</h4>
                  {channelStatus.sessions?.length > 0 ? (
                    <div className="ssai-mgr-sessions">
                      {channelStatus.sessions.map((s, i) => (
                        <div key={i} className="ssai-mgr-session-row">
                          <code>{s.id}</code>
                          <span>{s.adBreaks} ads</span>
                          <span className="ssai-mgr-muted">{s.lastActivity}</span>
                        </div>
                      ))}
                    </div>
                  ) : <div className="ssai-mgr-muted">No active sessions</div>}
                </div>

                {/* Stats */}
                <div className="ssai-mgr-card">
                  <h4><DollarSign size={14} /> Impression Stats</h4>
                  <div className="ssai-mgr-stat-row">
                    <span>Impressions</span>
                    <span className="ssai-mgr-stat-value">{channelStatus.stats?.impressions || 0}</span>
                  </div>
                  <div className="ssai-mgr-stat-row">
                    <span>Ad Breaks</span>
                    <span className="ssai-mgr-stat-value">{channelStatus.stats?.adBreaks || 0}</span>
                  </div>
                </div>
              </div>

              {/* Bumper Configuration */}
              <div className="ssai-mgr-card" style={{ marginBottom: 16 }}>
                <h4><Zap size={14} /> Bumper Configuration</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>
                    <input type="checkbox" checked={channelBumperEnabled} onChange={e => setChannelBumperEnabled(e.target.checked)} style={{ marginRight: 6 }} />
                    Enable bumpers
                  </label>
                  {channelBumperEnabled && (
                    <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      Position:
                      <select value={channelBumperPosition} onChange={e => setChannelBumperPosition(e.target.value)}
                        style={{ padding: '2px 6px', fontSize: 11, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                        <option value="both">Before & After</option>
                        <option value="before">Before Ad Only</option>
                        <option value="after">After Ad Only</option>
                      </select>
                    </label>
                  )}
                  {channelStatus?.channel?.bumpersCached > 0 && (
                    <span style={{ fontSize: 10, color: '#22c55e', background: 'rgba(34,197,94,0.1)', padding: '2px 6px', borderRadius: 3 }}>
                      {channelStatus.channel.bumpersCached} cached
                    </span>
                  )}
                </div>

                {bumpers.length > 0 ? (
                  <div style={{ marginBottom: 12 }}>
                    {bumpers.map(b => {
                      const isSelected = channelBumperIds.includes(b.id)
                      const formatColor = b.format === 'cf' ? '#f97316' : '#a855f7'
                      return (
                        <div key={b.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                          background: isSelected ? 'rgba(78,205,196,0.08)' : 'transparent',
                          borderRadius: 4, marginBottom: 4, cursor: 'pointer',
                          border: isSelected ? '1px solid rgba(78,205,196,0.3)' : '1px solid transparent',
                        }} onClick={() => toggleBumperForChannel(b.id)}>
                          <input type="checkbox" checked={isSelected} readOnly style={{ pointerEvents: 'none' }} />
                          <span style={{ fontSize: 12, flex: 1 }}>{b.name}</span>
                          <span style={{ fontSize: 9, color: formatColor, background: `${formatColor}15`, padding: '1px 5px', borderRadius: 3 }}>{b.format.toUpperCase()}</span>
                          {b.duration_seconds && <span style={{ fontSize: 10, color: '#999' }}>{b.duration_seconds}s</span>}
                          <button onClick={e => { e.stopPropagation(); handleDeleteBumper(b.id) }} style={{ background: 'none', border: 'none', color: '#e94560', cursor: 'pointer', fontSize: 12, padding: '0 4px' }}>×</button>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="ssai-mgr-muted" style={{ marginBottom: 12 }}>No bumpers in library. Add one below.</div>
                )}

                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveChannelBumpers} disabled={bumperSaving} style={{ fontSize: 11 }}>
                    {bumperSaving ? 'Saving...' : 'Save Bumper Config'}
                  </button>
                </div>
                {bumperStatus && <div style={{ fontSize: 11, color: bumperStatus.startsWith('Error') ? '#e94560' : '#22c55e', marginBottom: 8 }}>{bumperStatus}</div>}

                {/* Add new bumper */}
                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#999' }}>Add to Bumper Library</span>
                    <button className="btn btn-primary btn-sm" onClick={() => { setBumperPickerOpen(!bumperPickerOpen); if (!bumperPickerOpen) searchBumperVideos() }} style={{ fontSize: 11 }}>
                      {bumperPickerOpen ? 'Close Picker' : 'Browse Videos'}
                    </button>
                  </div>

                  {bumperPickerOpen && (
                    <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <select value={bumperPickerSource} onChange={e => { setBumperPickerSource(e.target.value); setBumperPickerResults([]) }}
                          style={{ padding: '4px 8px', fontSize: 11, background: 'var(--color-secondary)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                          <option value="mux">Mux Assets</option>
                          <option value="cf">CF Stream</option>
                        </select>
                        {bumperPickerSource === 'cf' && (
                          <input placeholder="Search CF Stream..." value={bumperPickerSearch} onChange={e => setBumperPickerSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && searchBumperVideos()}
                            style={{ flex: 1, padding: '4px 8px', fontSize: 11, background: 'var(--color-secondary)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={searchBumperVideos} style={{ fontSize: 11 }}>
                          {bumperPickerLoading ? 'Loading...' : 'Search'}
                        </button>
                      </div>
                      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {bumperPickerResults.length === 0 && !bumperPickerLoading && (
                          <div style={{ fontSize: 11, color: '#666', textAlign: 'center', padding: 12 }}>No results. Try searching or switch source.</div>
                        )}
                        {bumperPickerResults.map(v => (
                          <div key={v.id} onClick={() => selectBumperFromPicker(v)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 4, marginBottom: 2, ':hover': {} }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(78,205,196,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {v.thumbnail && <img src={v.thumbnail} alt="" style={{ width: 48, height: 27, objectFit: 'cover', borderRadius: 3 }} />}
                            <span style={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                            <span style={{ fontSize: 9, color: v.format === 'cf' ? '#f97316' : '#a855f7' }}>{v.format.toUpperCase()}</span>
                            {v.duration && <span style={{ fontSize: 10, color: '#999' }}>{Math.round(v.duration)}s</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Manual URL fallback */}
                  <details style={{ fontSize: 11, color: '#666' }}>
                    <summary style={{ cursor: 'pointer', marginBottom: 6 }}>Or enter URL manually</summary>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <input placeholder="Name" value={newBumperName} onChange={e => setNewBumperName(e.target.value)}
                        style={{ flex: 1, minWidth: 100, padding: '4px 8px', fontSize: 11, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                      <input placeholder="HLS URL" value={newBumperUrl} onChange={e => setNewBumperUrl(e.target.value)}
                        style={{ flex: 2, minWidth: 200, padding: '4px 8px', fontSize: 11, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                      <select value={newBumperFormat} onChange={e => setNewBumperFormat(e.target.value)}
                        style={{ padding: '4px 8px', fontSize: 11, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                        <option value="mux">Mux</option>
                        <option value="cf">CF Stream</option>
                      </select>
                      <input placeholder="Duration (s)" value={newBumperDuration} onChange={e => setNewBumperDuration(e.target.value)}
                        style={{ width: 70, padding: '4px 8px', fontSize: 11, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4 }} />
                      <button className="btn btn-primary btn-sm" onClick={handleAddBumper} disabled={!newBumperName || !newBumperUrl} style={{ fontSize: 11 }}>Add</button>
                    </div>
                  </details>
                </div>
              </div>

              {/* Manifest Inspector */}
              <div className="ssai-mgr-manifest">
                <div className="ssai-mgr-manifest-header">
                  <h4>Manifest Inspector (video_720)</h4>
                  <button className="btn btn-ghost btn-sm" onClick={() => fetchManifest(selectedChannel)}>
                    <RefreshCw size={12} /> Refresh
                  </button>
                </div>
                <div className="ssai-mgr-manifest-body">
                  {!manifest ? (
                    <div className="ssai-mgr-muted" style={{ padding: 20, textAlign: 'center' }}>Loading manifest...</div>
                  ) : (
                    <pre className="ssai-mgr-manifest-pre">
                      {manifestLines.map(({ line, type, num }) => (
                        <div key={num} className={`ssai-mgr-manifest-line ${type}`}>
                          <span className="ssai-mgr-line-num">{num}</span>
                          <span className="ssai-mgr-line-text">{line || ' '}</span>
                        </div>
                      ))}
                    </pre>
                  )}
                </div>
                <div className="ssai-mgr-manifest-legend">
                  <span className="legend-header">Header</span>
                  <span className="legend-disc">DISCONTINUITY</span>
                  <span className="legend-cue-out">CUE-OUT (ad start)</span>
                  <span className="legend-cue-in">CUE-IN (ad end)</span>
                  <span className="legend-map">MAP (init seg)</span>
                  <span className="legend-url">Segment URL</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
